import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import { HiOutlineMenu, HiOutlineX } from "react-icons/hi";

export const domains = [
  { name: "RTL & Architecture", id: "rtl-architecture" },
  { name: "Design Verification", id: "design-verification" },
  { name: "DFT", id: "dft" },
  { name: "Physical Design & STA", id: "physical-design-sta" },
  { name: "FPGA & Emulation", id: "fpga-emulation" },
  { name: "Silicon & Embedded", id: "silicon-embedded" },
];

const navItems = [
  {
    name: "Solutions",
    to: "/services",
    activePaths: ["/services", "/startup-workforce"],
    children: [
      { name: "Semiconductor Engineering", to: "/services", note: "SOW, augmentation and BOT across the silicon lifecycle" },
      { name: "Startup Workforce", to: "/startup-workforce", note: "Plan, build and operate your team" },
    ],
  },
  {
    name: "Capabilities",
    to: "/services",
    activePaths: [],
    children: domains.map((d) => ({ name: d.name, to: `/services#${d.id}` })),
  },
  { name: "How We Engage", anchor: "how-we-engage" },
  {
    name: "About",
    to: "/company",
    activePaths: ["/company", "/about"],
    align: "right",
    children: [
      { name: "Company & leadership", to: "/company", note: "Our story, team and operating principles" },
      { name: "Mission & values", to: "/about", note: "What we stand for" },
    ],
  },
  { name: "Insights", to: "/news-blogs" },
  {
    name: "Careers",
    to: "/candidates",
    activePaths: ["/candidates", "/careers"],
    align: "right",
    children: [
      { name: "Candidate Hub", to: "/candidates", note: "Job alerts and current opportunities" },
      { name: "Open positions", to: "/careers", note: "Roles open at Chiparama" },
    ],
  },
];

const linkBase =
  "relative inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobileSection, setMobileSection] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpen(null);
    setMobileSection(null);
  }, [location.pathname, location.hash]);

  const openMenu = (name) => {
    clearTimeout(closeTimer.current);
    setOpen(name);
  };
  const closeMenu = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 140);
  };
  const toggleMenu = (name) => {
    clearTimeout(closeTimer.current);
    setOpen((cur) => (cur === name ? null : name));
  };

  // Close on Escape or when clicking/tapping outside the menu bar.
  const navRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    const onPointer = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
      clearTimeout(closeTimer.current);
    };
  }, []);

  const goToAnchor = (id) => {
    setMobileOpen(false);
    const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname === "/") scroll();
    else {
      navigate("/");
      setTimeout(scroll, 350);
    }
  };

  const isActive = (item) => {
    const paths = item.activePaths || (item.to ? [item.to] : []);
    return paths.some((p) => location.pathname.startsWith(p));
  };

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      className={`fixed inset-x-0 top-0 z-50 border-b bg-navy-950/90 backdrop-blur-xl transition-colors ${
        scrolled ? "border-white/10 bg-navy-950/90" : "border-white/[0.06]"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Chiparama home">
          <img src="/chiparamalogo.webp" alt="" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5" width="36" height="36" />
          <span className="font-display text-lg font-semibold tracking-tight text-white">Chiparama</span>
        </Link>

        {/* Desktop */}
        <ul className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => {
            const active = isActive(item);
            const isOpen = open === item.name;
            return (
              <li
                key={item.name}
                className="relative"
                onMouseEnter={() => (item.children ? openMenu(item.name) : setOpen(null))}
                onMouseLeave={() => item.children && closeMenu()}
                onFocus={() => (item.children ? openMenu(item.name) : setOpen(null))}
                onBlur={(e) => {
                  if (item.children && !e.currentTarget.contains(e.relatedTarget)) closeMenu();
                }}
              >
                {item.anchor ? (
                  <button
                    onClick={() => goToAnchor(item.anchor)}
                    className={`${linkBase} text-slate-300 hover:text-white`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <div className="flex items-center">
                    <Link
                      to={item.to}
                      className={`${linkBase} ${item.children ? "!pr-1" : ""} ${
                        active || isOpen ? "text-white" : "text-slate-300 hover:text-white"
                      }`}
                      aria-current={active ? "page" : undefined}
                    >
                      {item.name}
                      {active && <span className="absolute inset-x-3 -bottom-0.5 h-px bg-brand-light" aria-hidden="true" />}
                    </Link>
                    {item.children && (
                      <button
                        type="button"
                        onClick={() => toggleMenu(item.name)}
                        aria-label={`${item.name} menu`}
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        className={`-ml-1 rounded-md p-2 transition-colors ${
                          active || isOpen ? "text-white" : "text-slate-500 hover:text-white"
                        }`}
                      >
                        <FaChevronDown
                          className={`h-2.5 w-2.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>
                )}

                {item.children && isOpen && (
                  <div className={`absolute top-full z-50 pt-2 ${item.align === "right" ? "right-0" : "left-0"}`}>
                    <ul className="w-72 rounded-2xl border border-white/10 bg-navy-900/95 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl">
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.to}
                            onClick={() => setOpen(null)}
                            className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-white/[0.06] focus-visible:bg-white/[0.06]"
                          >
                            <span className="block text-sm font-medium text-white">{child.name}</span>
                            {child.note && <span className="mt-0.5 block text-xs text-slate-400">{child.note}</span>}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="btn-primary hidden !py-2.5 !text-[13px] lg:inline-flex">
            Talk to Chiparama
          </Link>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-md p-2 text-white lg:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <HiOutlineX className="h-6 w-6" /> : <HiOutlineMenu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-72px)] overflow-y-auto border-t border-white/10 bg-navy-950 lg:hidden">
          <ul className="container-x space-y-1 py-4">
            {navItems.map((item) => (
              <li key={item.name}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => setMobileSection(mobileSection === item.name ? null : item.name)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base font-medium text-white"
                      aria-expanded={mobileSection === item.name}
                    >
                      {item.name}
                      <FaChevronDown
                        className={`h-3 w-3 text-slate-400 transition-transform ${
                          mobileSection === item.name ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {mobileSection === item.name && (
                      <ul className="mb-2 ml-3 space-y-0.5 border-l border-white/10 pl-3">
                        {item.children.map((child) => (
                          <li key={child.name}>
                            <Link to={child.to} className="block rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:text-white">
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : item.anchor ? (
                  <button
                    onClick={() => goToAnchor(item.anchor)}
                    className="block w-full rounded-lg px-3 py-3 text-left text-base font-medium text-white"
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link to={item.to} className="block rounded-lg px-3 py-3 text-base font-medium text-white">
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
            <li className="pt-3">
              <Link to="/contact" className="btn-primary w-full">
                Talk to Chiparama
              </Link>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
